/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {Calendar, ConfigProvider, Select, theme} from 'antd'
import type {Dayjs} from 'dayjs'
import dayjs from 'dayjs'
import clsx from 'clsx'
import {useTranslation} from 'react-i18next'
import localeData from 'dayjs/plugin/localeData'

import {localeObject} from 'utils/localeUtils'
import {LOCALE} from 'helpers/config'
import UiIconButton from './UiIconButton'
import UiIcon from './UiIcon'
import useMonthOptions from './UiDatePickerInlineComponents/useMonthOptions'

import 'dayjs/locale/de'
import 'dayjs/locale/de-ch'
import 'dayjs/locale/en'
import 'dayjs/locale/nl'
import 'dayjs/locale/sk'
import 'dayjs/locale/hu'
import 'dayjs/locale/it'

dayjs.extend(localeData)

type UiDatePickerInlineProps = {
    value?: Date | string | null
    onChange?: (date: Date | null) => void
    className?: string
    minDate?: Date
    maxDate?: Date
    label?: string
}

const UiDatePickerInline: React.FC<UiDatePickerInlineProps> = ({
    value,
    onChange,
    className,
    minDate,
    maxDate,
    label,
}) => {
    const { t } = useTranslation()
    const { token } = theme.useToken()
    const containerRef = React.useRef<HTMLDivElement>(null)
    const monthOptions = useMonthOptions()
    const val = value ? dayjs(value) : undefined
    const [viewDate, setViewDate] = React.useState<Dayjs>(val || dayjs())

    // Sync external value changes to view date
    React.useEffect(() => {
        if (value) {
            setViewDate(dayjs(value))
        }
    }, [value])

    const disabledDate = (current: Dayjs) => {
        if (minDate && current.isBefore(dayjs(minDate), 'day')) {
            return true
        }
        if (maxDate && current.isAfter(dayjs(maxDate), 'day')) {
            return true
        }
        return false
    }

    const onSelect = (
        date: Dayjs,
        { source }: { source: 'year' | 'month' | 'date' | 'customize' }
    ) => {
        setViewDate(date)
        if (source === 'date') {
            onChange?.(date.toDate())
        }
    }

    const headerRender = React.useCallback(
        ({
            value: headerValue,
            onChange: onCalendarChange,
        }: {
            value: Dayjs
            onChange: (date: Dayjs) => void
        }) => {
            const currentYear = dayjs().year()
            const yearOptions = []
            for (let i = currentYear - 50; i <= currentYear + 50; i++) {
                yearOptions.push({ label: i.toString(), value: i })
            }

            const onYearChange = (newYear: number) => {
                const nextValue = headerValue.clone().year(newYear)
                onCalendarChange(nextValue)
                setViewDate(nextValue)
            }

            const onMonthChange = (newMonth: number) => {
                const nextValue = headerValue.clone().month(newMonth)
                onCalendarChange(nextValue)
                setViewDate(nextValue)
            }

            const onPrevMonth = (e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                const nextValue = headerValue.clone().subtract(1, 'month')
                onCalendarChange(nextValue)
                setViewDate(nextValue)
            }

            const onNextMonth = (e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                const nextValue = headerValue.clone().add(1, 'month')
                onCalendarChange(nextValue)
                setViewDate(nextValue)
            }

            const onToday = (e: React.MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                const today = dayjs()
                onCalendarChange(today)
                setViewDate(today)
                onChange?.(today.toDate())
            }

            return (
                <div className="UiDatePickerInlineHeader flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
                    <div className="flex items-center gap-1.5 focus-within:z-10">
                        <Select
                            size="small"
                            classNames={{ popup: { root: 'dark:bg-gray-900' } }}
                            popupMatchSelectWidth={false}
                            getPopupContainer={() =>
                                containerRef.current || document.body
                            }
                            value={headerValue.month()}
                            options={monthOptions}
                            onChange={onMonthChange}
                            variant="borderless"
                        />
                        <Select
                            size="small"
                            classNames={{ popup: { root: 'dark:bg-gray-900' } }}
                            popupMatchSelectWidth={false}
                            getPopupContainer={() =>
                                containerRef.current || document.body
                            }
                            value={headerValue.year()}
                            options={yearOptions}
                            onChange={onYearChange}
                            variant="borderless"
                        />
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={onToday}
                            className={clsx(
                                'text-[10px] font-bold px-2.5 py-1.5 rounded-lg transition-all cursor-pointer uppercase tracking-wider',
                                'text-cta-600 hover:text-cta-700 bg-cta-100/50 hover:bg-cta-100',
                                'dark:text-cta-400 dark:hover:text-cta-300 dark:bg-cta-900/40 dark:hover:bg-cta-900/60'
                            )}
                        >
                            {t('Today')}
                        </button>
                        <div className="flex items-center ml-1">
                            <UiIconButton
                                variant="ghost"
                                size="sm"
                                onClick={onPrevMonth}
                                className="h-7! w-7! hover:bg-gray-200/50 dark:hover:bg-gray-800"
                            >
                                <UiIcon name="chevron-left" className="w-4 h-4" />
                            </UiIconButton>
                            <UiIconButton
                                variant="ghost"
                                size="sm"
                                onClick={onNextMonth}
                                className="h-7! w-7! hover:bg-gray-200/50 dark:hover:bg-gray-800"
                            >
                                <UiIcon name="chevron-right" className="w-4 h-4" />
                            </UiIconButton>
                        </div>
                    </div>
                </div>
            )
        },
        [monthOptions, t, onChange]
    )

    const dateCellRender = (date: Dayjs) => {
        const isSelected = val && date.isSame(val, 'day')
        const isToday = date.isSame(dayjs(), 'day')
        const isDisabled = disabledDate(date)
        const isSameMonth = date.isSame(viewDate, 'month')

        return (
            <div
                className={clsx(
                    'flex items-center justify-center h-full w-full py-0.5 cursor-pointer transition-all duration-200 group relative',
                    isDisabled &&
                        'cursor-not-allowed opacity-20 pointer-events-none'
                )}
            >
                <div
                    className={clsx(
                        'flex items-center justify-center w-8 h-8 rounded-lg text-xs font-bold transition-all relative',
                        isSelected
                            ? 'bg-cta-light text-white shadow-md'
                            : isToday
                              ? 'text-cta-600 dark:text-cta-400 ring-1 ring-cta-100 dark:ring-cta-900/30'
                              : isSameMonth
                                ? 'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800'
                                : 'text-gray-400 dark:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    )}
                >
                    {date.date()}
                    {isToday && !isSelected && (
                        <div className="absolute bottom-1 w-1 h-1 rounded-full bg-cta-500" />
                    )}
                </div>
            </div>
        )
    }

    return (
        <div
            className={clsx(
                'UiDatePickerInline flex w-full flex-col gap-2',
                className
            )}
        >
            {label && (
                <div
                    className={clsx(
                        'text-[11px] font-bold text-gray-500 dark:text-gray-400 ml-1 uppercase tracking-wider'
                    )}
                >
                    {t(label)}
                </div>
            )}
            <div
                ref={containerRef}
                className={clsx(
                    'w-full rounded-xl overflow-hidden shadow-sm bg-white dark:bg-gray-950',
                    'border border-gray-200 dark:border-gray-800'
                )}
            >
                <ConfigProvider
                    locale={localeObject(LOCALE)}
                    theme={{
                        algorithm:
                            token.colorBgContainer === 'transparent'
                                ? theme.darkAlgorithm
                                : theme.defaultAlgorithm,
                        token: {
                            colorPrimary: '#0c97a3',
                            borderRadius: 8,
                            fontFamily: 'inherit',
                        },
                        components: {
                            Calendar: {
                                fullPanelBg: 'transparent',
                                itemActiveBg: 'transparent',
                            },
                        },
                    }}
                >
                    <Calendar
                        fullscreen={false}
                        value={viewDate}
                        onSelect={onSelect}
                        disabledDate={disabledDate}
                        headerRender={headerRender}
                        fullCellRender={dateCellRender}
                        style={{ background: 'transparent' }}
                    />
                </ConfigProvider>
            </div>
        </div>
    )
}

export default UiDatePickerInline
